import type { MouseEvent } from 'react';
import { useI18n } from '@openclassrooms/translations/I18nProvider';
import { ROUTES } from '@openclassrooms/routing';
import { styled } from '@classify/react-core-components/styles';
import { useLocalisationConfig } from '@openclassrooms/localisation/LocalisationConfigProvider';

import FooterLinkItem from './FooterLinkItem';

declare global {
  interface Window {
    truste?: {
      eu?: { clickListener: () => void };
    };
  }
}

const List = styled('ul')(({ theme: { spacing, palette } }) => ({
  marginBottom: spacing(2),
  display: 'flex',
  flexWrap: 'wrap',
  listStyle: 'none',
  margin: spacing(4, 0),
  padding: 0,
  columnGap: spacing(4),
  rowGap: spacing(1),
  position: 'relative',
  '&::before': {
    content: '""',
    width: '100%',
    top: -16,
    borderTop: `1px solid ${palette.divider}`,
    position: 'absolute',
  },
}));

export default function FooterPolicies() {
  const { translate } = useI18n();
  const { getLocalisationConfig } = useLocalisationConfig();

  const handleCookieClick = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    window.truste?.eu?.clickListener();
  };

  const specificCountryUrl = getLocalisationConfig('page.policies_specific_country.url');
  const apprenticeshipAgreementUrl = getLocalisationConfig('apprenticeship.agreement.url');

  return (
    <div>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 208.2 15.8"
        width="208"
        fill="var(--clf-brand-official)"
        aria-hidden="true"
      >
        <path d="M126.5 10.8c0 .9-.2 1.8-.7 2.5-.5.7-1.2 1.3-2.1 1.8-1 .4-2.1.7-3.5.7-1.2 0-2.3-.1-3.4-.4-1.1-.3-2-.7-2.7-1.2l2.2-2.6c.4.2.9.4 1.4.6.9.3 1.7.4 2.5.4 1.5 0 2.4-.4 2.4-1.2 0-.4-.2-.7-.7-1-.4-.2-1.2-.4-2.1-.7-1.1-.2-2-.5-2.7-.8-.7-.3-1.3-.7-1.9-1.3-.5-.6-.8-1.5-.8-2.5s.2-1.8.7-2.5c.5-.7 1.2-1.3 2.1-1.8 1-.4 2.1-.7 3.5-.7 1 0 1.9.1 2.8.4 1 .2 1.8.6 2.4 1l-2.3 2.7c-1-.4-2.1-.7-3-.7-.8 0-1.4.1-1.8.4-.4.2-.6.6-.6 1s.2.7.7.9c.4.2 1.2.4 2.1.7.9.2 1.8.5 2.7.8.7.3 1.3.7 1.9 1.3.6.3.9 1.2.9 2.2zm81.6 0c0 .9-.2 1.8-.7 2.5s-1.2 1.3-2.1 1.8c-1 .4-2.1.7-3.5.7-1.2 0-2.3-.1-3.4-.4-1.1-.3-2-.7-2.7-1.2l2.2-2.6c.4.2.9.4 1.4.6.9.3 1.7.4 2.5.4 1.5 0 2.4-.4 2.4-1.2 0-.4-.2-.7-.7-1-.4-.2-1.2-.4-2.1-.7-.9-.2-1.8-.5-2.7-.8-.7-.3-1.3-.7-1.9-1.3-.5-.6-.8-1.5-.8-2.5s.2-1.8.7-2.5c.5-.7 1.2-1.3 2.1-1.8 1-.4 2.1-.7 3.5-.7 1 0 1.9.1 2.8.4 1 .2 1.8.6 2.4 1l-2.3 2.7c-1-.4-2.1-.7-3-.7-.8 0-1.4.1-1.8.4-.4.2-.6.6-.6 1s.2.7.7.9c.4.2 1.2.4 2.1.7.9.2 1.8.5 2.7.8.7.3 1.3.7 1.9 1.3.7.3.9 1.2.9 2.2zm-180.6-5a5.1 5.1 0 0 1-3 4.8c-1 .4-2.1.7-3.4.7h-2.5v4h-4.1V.1h6.6c1.3 0 2.4.2 3.4.7 1 .4 1.7 1.1 2.2 2 .5.9.8 1.9.8 3zm-4.2 0c0-.7-.2-1.2-.7-1.6-.4-.4-1-.6-1.9-.6h-2.3v4.5h2.3c.8 0 1.5-.2 1.9-.6.5-.3.7-.9.7-1.7zm17.4 6.3v3.3H29.2V.3h11.3v3.3h-7.2v2.6H39v3.2h-5.8v2.8l7.5-.1zM56.4.3v15.1H53l-6.4-8.1v8.1h-4.1V.3h3.4l6.4 8.1V.3h4.1zM82.6 12v3.4H72.3V.2h4.1v11.7l6.2.1zm17.7 3.4H96l-1.1-2.9h-6.1l-1.1 2.9h-4.2L89.9.3H94l6.3 15.1zm-6.6-6.1-1.8-4.8L90 9.3h3.7zm19.5 1.5c0 .9-.2 1.8-.7 2.5-.5.7-1.2 1.3-2.1 1.8-1 .4-2.1.7-3.5.7-1.2 0-2.3-.1-3.4-.4s-2-.7-2.7-1.2l2.2-2.6c.4.2.9.4 1.4.6.9.3 1.7.4 2.5.4 1.5 0 2.4-.4 2.4-1.2 0-.4-.2-.7-.7-1-.4-.2-1.2-.4-2.1-.7-1.1-.2-2-.5-2.7-.8-.7-.3-1.3-.7-1.9-1.3-.5-.6-.8-1.5-.8-2.5s.2-1.8.7-2.5c.5-.7 1.2-1.3 2.1-1.8 1-.4 2.1-.7 3.5-.7 1 0 1.9.1 2.8.4 1 .2 1.7.6 2.4 1l-2.3 2.7c-1-.4-2.1-.7-3-.7-.8 0-1.4.1-1.8.4-.4.2-.6.6-.6 1s.2.7.7.9c.4.2 1.2.4 2.1.7.9.2 1.8.5 2.7.8.7.3 1.3.7 1.9 1.3.6.3.9 1.2.9 2.2zm-100.5-3A7.94 7.94 0 0 1 .1 14.2L2.8 11a3.78 3.78 0 0 0 5.8-3.1 3.78 3.78 0 0 0-5.8-3.1L.1 1.6C1.4.6 3.1.1 4.7.1c4.5-.1 8 3.4 8 7.7zM68 11l2.7 3.1c-1.3 1-3 1.5-4.6 1.5-4.4 0-8-3.5-8-7.9a7.94 7.94 0 0 1 12.6-6.4L68 4.6a3.78 3.78 0 0 0-5.8 3.1c0 2 1.7 3.7 3.8 3.7.8.2 1.4 0 2-.4zm73.6 4.4-3.2-4.7c1-.4 1.6-1.1 2.1-1.9a5.83 5.83 0 0 0 0-5.9c-.5-.9-1.3-1.5-2.2-2-1-.4-2.1-.7-3.4-.7h-6.6v15.1h4.1v-4h1.9l2.7 4 4.6.1zm-9.3-11.8h2.4c.8 0 1.5.2 1.9.6.4.4.7 1 .7 1.6 0 .7-.2 1.2-.7 1.6-.4.4-1 .6-1.9.6h-2.4V3.6zM150.8 0c4.4 0 8 3.5 8 7.9s-3.5 7.9-8 7.9c-4.4 0-8-3.5-8-7.9s3.6-7.9 8-7.9zm-4.1 7.8c0 2.2 1.8 4 4 4s4-1.8 4-4-1.8-4-4-4c-2.1.1-4 1.8-4 4zM167.9 0c4.4 0 8 3.5 8 7.9s-3.5 7.9-8 7.9c-4.4 0-8-3.5-8-7.9s3.7-7.9 8-7.9zm-4 7.8c0 2.2 1.8 4 4 4s4-1.8 4-4-1.8-4-4-4c-2.2.1-4 1.8-4 4zm21.9 2.1L181 .3h-3.4v15.1h3.6V8.8l3.6 6.7h2.1l3.6-6.7v6.7h3.6V.3h-3.4l-4.9 9.6z" />
      </svg>

      <List>
        <FooterLinkItem path={ROUTES.POLICIES.LEGAL_INFORMATION} isSmall>
          {translate('footer.more.legal_information')}
        </FooterLinkItem>
        <FooterLinkItem path={ROUTES.POLICIES.TERMS_CONDITION} isSmall>
          {translate('footer.more.terms_use')}
        </FooterLinkItem>
        <FooterLinkItem path={ROUTES.POLICIES.PRIVACY_POLICY} isSmall>
          {translate('footer.more.privacy_policy')}
        </FooterLinkItem>
        <FooterLinkItem onClick={handleCookieClick} isSmall>
          {translate('footer.more.cookie')}
        </FooterLinkItem>
        <FooterLinkItem path={String(getLocalisationConfig('page.accessibility.url'))} isSmall>
          {translate('footer.more.accessibility')}
        </FooterLinkItem>
        {specificCountryUrl && (
          <FooterLinkItem path={String(specificCountryUrl)} isSmall>
            {translate('footer.more.country_specific_policies')}
          </FooterLinkItem>
        )}
        {apprenticeshipAgreementUrl && (
          <FooterLinkItem path={String(apprenticeshipAgreementUrl)} isSmall>
            {translate('footer.more.apprenticeship_agreement')}
          </FooterLinkItem>
        )}
        <FooterLinkItem path={ROUTES.SECURITY.SECURITY_PAGE} isSmall>
          {translate('security_page.title-1.security')}
        </FooterLinkItem>
      </List>
    </div>
  );
}
